const CURRENCY_SYMBOL_TO_REGION = {
  '£': 'UK',
  '€': 'EU',
  '$': 'US',
}

const REGION_TO_SYMBOL = {
  UK: '£',
  EU: '€',
  US: '$',
}

/**
 * @param {Array<{ timestamp?: string }>} tickets
 * @returns {string|null}
 */
export function getEarliestTicketTimestamp(tickets) {
  if (!tickets || tickets.length === 0) {
    return null
  }

  const timestamps = tickets
    .map((ticket) => ticket.timestamp)
    .filter((timestamp) => timestamp)
    .sort()

  return timestamps.length > 0 ? timestamps[0] : null
}

/**
 * Earliest ticket timestamp across all event/payment rows.
 *
 * @param {Array<{ tickets?: Array<{ timestamp?: string }> }>} payments
 * @returns {string|null}
 */
export function getEarliestTicketAcrossPayments(payments) {
  if (!payments?.length) {
    return null
  }

  const timestamps = payments
    .filter((payment) => payment?.tickets?.length)
    .map((payment) => getEarliestTicketTimestamp(payment.tickets))
    .filter(Boolean)
    .sort()

  return timestamps.length > 0 ? timestamps[0] : null
}

/**
 * @param {{ stripe_connect?: { status?: number, updated_at?: string } }} user
 * @param {Array<{ tickets?: Array<{ timestamp?: string }> }>} payments
 * @returns {boolean}
 */
export function isOrganiserHybridPayments(user, payments) {
  if (!user?.stripe_connect || user.stripe_connect.status !== 1) {
    return false
  }

  const stripeUpdatedAt = user.stripe_connect.updated_at
  if (!stripeUpdatedAt || !payments?.length) {
    return false
  }

  const earliest = getEarliestTicketAcrossPayments(payments)
  if (!earliest) {
    return false
  }

  return new Date(earliest) < new Date(stripeUpdatedAt)
}

/**
 * @param {string|null|undefined} timestamp
 * @param {string|null|undefined} stripeConnectUpdatedAt
 * @returns {boolean}
 */
export function isTimestampAfterStripeConnect(timestamp, stripeConnectUpdatedAt) {
  if (!timestamp || !stripeConnectUpdatedAt) {
    return false
  }
  return new Date(timestamp) > new Date(stripeConnectUpdatedAt)
}

/**
 * @param {string|number|null|undefined} price
 * @returns {number|null}
 */
export function parseUnitPrice(price) {
  if (price === null || price === undefined || price === '') {
    return null
  }
  const n = parseFloat(String(price).replace(/,/g, ''), 10)
  return Number.isFinite(n) ? n : null
}

/**
 * @param {{ quantity?: string|number }} ticket
 * @returns {number}
 */
function ticketQuantity(ticket) {
  const q = parseInt(String(ticket.quantity), 10)
  return Number.isFinite(q) && q > 0 ? q : 1
}

/**
 * Split event owed amount by ticket sale timestamps (quantity × unit price).
 *
 * @param {{ tickets?: Array<{ timestamp?: string, quantity?: string|number }>, owed?: string|number, price?: string|number }} payment
 * @param {string} stripeConnectUpdatedAt
 * @returns {{ unpaid: number, paidViaStripe: number }}
 */
export function splitPaymentOwedByTickets(payment, stripeConnectUpdatedAt) {
  const totalOwed = parseOwedAmount(payment?.owed)
  if (totalOwed <= 0) {
    return { unpaid: 0, paidViaStripe: 0 }
  }

  const tickets = payment?.tickets || []
  if (!tickets.length) {
    return { unpaid: totalOwed, paidViaStripe: 0 }
  }

  let postStripeQty = 0
  let preStripeQty = 0

  for (const ticket of tickets) {
    const qty = ticketQuantity(ticket)
    if (!ticket.timestamp) {
      preStripeQty += qty
      continue
    }
    if (isTimestampAfterStripeConnect(ticket.timestamp, stripeConnectUpdatedAt)) {
      postStripeQty += qty
    } else {
      preStripeQty += qty
    }
  }

  const unitPrice = parseUnitPrice(payment.price)
  if (unitPrice !== null && unitPrice > 0) {
    let unpaid = preStripeQty * unitPrice
    let paidViaStripe = postStripeQty * unitPrice
    const sum = unpaid + paidViaStripe

    if (sum > 0 && Math.abs(sum - totalOwed) > 0.02) {
      const scale = totalOwed / sum
      unpaid = Math.round(unpaid * scale * 100) / 100
      paidViaStripe = Math.round(paidViaStripe * scale * 100) / 100
      const remainder = Math.round((totalOwed - unpaid - paidViaStripe) * 100) / 100
      if (remainder !== 0) {
        unpaid = Math.round((unpaid + remainder) * 100) / 100
      }
    }

    return { unpaid, paidViaStripe }
  }

  const totalQty = preStripeQty + postStripeQty
  if (totalQty <= 0) {
    return { unpaid: totalOwed, paidViaStripe: 0 }
  }

  const paidViaStripe = Math.round((postStripeQty / totalQty) * totalOwed * 100) / 100
  const unpaid = Math.round((totalOwed - paidViaStripe) * 100) / 100
  return { unpaid, paidViaStripe }
}

/**
 * Event row is fully settled via Stripe when every ticket with a timestamp is after Connect updated.
 *
 * @param {{ tickets?: Array<{ timestamp?: string }> }} payment
 * @param {string|null|undefined} stripeConnectUpdatedAt
 * @returns {boolean}
 */
export function isPaymentPaidViaStripe(payment, stripeConnectUpdatedAt) {
  const tickets = payment?.tickets || []
  const withTimestamp = tickets.filter((ticket) => ticket.timestamp)
  if (!withTimestamp.length) {
    return false
  }
  return withTimestamp.every((ticket) =>
    isTimestampAfterStripeConnect(ticket.timestamp, stripeConnectUpdatedAt)
  )
}

/**
 * @param {string|number|null|undefined} owed
 * @returns {number}
 */
function parseOwedAmount(owed) {
  if (owed === null || owed === undefined || owed === '') {
    return 0
  }
  const n = parseFloat(String(owed).replace(/,/g, ''), 10)
  return Number.isFinite(n) ? n : 0
}

/**
 * @param {string|undefined} currencySymbol
 * @returns {string|null}
 */
function regionFromCurrencySymbol(currencySymbol) {
  if (!currencySymbol) {
    return null
  }
  return CURRENCY_SYMBOL_TO_REGION[currencySymbol] || null
}

function addToRegionBucket(bucket, region, amount) {
  if (amount <= 0) {
    return
  }
  bucket[region] = Math.round(((bucket[region] || 0) + amount) * 100) / 100
}

/**
 * @param {Array<object>} payments
 * @param {string} stripeConnectUpdatedAt
 * @returns {{ unpaid: Record<string, number>, paidViaStripe: Record<string, number> }}
 */
export function computeHybridPaymentBreakdown(payments, stripeConnectUpdatedAt) {
  const unpaid = {}
  const paidViaStripe = {}

  if (!payments?.length || !stripeConnectUpdatedAt) {
    return { unpaid, paidViaStripe }
  }

  for (const payment of payments) {
    if (!payment || payment.id === 'Total') {
      continue
    }

    const region = regionFromCurrencySymbol(payment.currency_symbol)
    if (!region) {
      continue
    }

    const { unpaid: unpaidAmount, paidViaStripe: stripeAmount } = splitPaymentOwedByTickets(
      payment,
      stripeConnectUpdatedAt
    )

    addToRegionBucket(unpaid, region, unpaidAmount)
    addToRegionBucket(paidViaStripe, region, stripeAmount)
  }

  return { unpaid, paidViaStripe }
}

/**
 * @param {number} amount
 * @returns {string}
 */
function formatMoneyAmount(amount) {
  const rounded = Math.round(amount * 100) / 100
  return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(2)
}

/**
 * @param {Record<string, number>} amountsByRegion
 * @returns {string}
 */
function formatAmountsByRegion(amountsByRegion) {
  return Object.entries(amountsByRegion || {})
    .filter(([, amount]) => amount > 0)
    .map(([region, amount]) => `${REGION_TO_SYMBOL[region] || ''}${formatMoneyAmount(amount)}`)
    .join(' · ')
}

/**
 * @param {{ unpaid: Record<string, number>, paidViaStripe: Record<string, number> }} breakdown
 * @param {Record<string, number>} totalOwed
 * @returns {string}
 */
export function formatHybridTotalOwedDisplay(breakdown, totalOwed) {
  const safeBreakdown = breakdown || { unpaid: {}, paidViaStripe: {} }
  const unpaidLabel = formatAmountsByRegion(safeBreakdown.unpaid)
  const stripeLabel = formatAmountsByRegion(safeBreakdown.paidViaStripe)
  const totalLabel = formatAmountsByRegion(totalOwed || {})

  if (!unpaidLabel && !stripeLabel) {
    return totalLabel || ''
  }

  const parts = []
  if (unpaidLabel) {
    parts.push(`Unpaid: ${unpaidLabel}`)
  }
  if (stripeLabel) {
    parts.push(`${stripeLabel} via Stripe`)
  }
  if (totalLabel && (unpaidLabel || stripeLabel)) {
    parts.push(`${totalLabel} total`)
  }

  return parts.join(' · ')
}
